/**
 * Enum-to-label helpers shared by every table cell, group header and card
 * that can receive a raw stored value (`ACTIVE`, `ON_HOLD`, `SOW`) where a
 * configured option (`active`, `on-hold`, `sow`) or a display name is
 * expected. Option lists are keyed by lowercase kebab ids while legacy rows
 * still carry the uppercase enum, so lookups go through `normalizeOptionKey`
 * on both sides, and the last-resort fallback is a Title Case rendering of
 * the constant rather than the constant itself.
 */

/** `ON_HOLD`, `on-hold`, `On Hold` and `on hold` all normalise to `on-hold`. */
export function normalizeOptionKey(
  value: string | number | null | undefined,
): string {
  if (value === null || value === undefined) return ''
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
}

/** True when the value is a machine constant (`ACTIVE`, `ON_HOLD`, `on-hold`, `snake_case`). */
export function isMachineConstant(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false
  if (/^[A-Z0-9]+(?:[_\s-][A-Z0-9]+)*$/.test(trimmed)) return true
  return /^[a-z0-9]+(?:[_-][a-z0-9]+)+$/.test(trimmed)
}

/**
 * `ON_HOLD` / `on-hold` / `ACTIVE` → `On Hold` / `On Hold` / `Active`.
 * Values that already read as a label (`Statement of Work`, `Not Started`)
 * come back unchanged so configured names are never re-cased.
 */
export function formatEnumLabel(
  value: string | number | null | undefined,
): string {
  if (value === null || value === undefined) return ''
  const raw = String(value).trim()
  if (!raw) return ''
  if (!isMachineConstant(raw)) return raw
  return raw
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(part => AMERICAN_SPELLINGS[part] ?? part)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/**
 * Enum values keep their stored spelling (`CANCELLED`), but product copy is
 * American English, so the generic fallback label swaps the few words that
 * differ. Explicit label maps still win over this.
 */
const AMERICAN_SPELLINGS: Record<string, string> = {
  cancelled: 'canceled',
  cancelling: 'canceling',
}

/**
 * Find an option whose value matches the stored value ignoring case and
 * separator style. Exact matches win so two options that only differ by
 * case (rare, but legal) resolve deterministically.
 */
export function findOptionByValue<T extends { value: string | number }>(
  options: readonly T[] | null | undefined,
  value: string | number | null | undefined,
): T | undefined {
  if (!options?.length || value === null || value === undefined)
    return undefined
  const stringValue = String(value)
  const exact = options.find(option => String(option.value) === stringValue)
  if (exact) return exact
  const key = normalizeOptionKey(stringValue)
  if (!key) return undefined
  return options.find(option => normalizeOptionKey(option.value) === key)
}

/**
 * Label for a stored value: the matching option's label when one exists,
 * otherwise a Title Case rendering of the raw value.
 */
export function resolveOptionLabel<
  T extends { value: string | number; label?: string | null },
>(
  options: readonly T[] | null | undefined,
  value: string | number | null | undefined,
): string {
  const option = findOptionByValue(options, value)
  if (option) return option.label || formatEnumLabel(String(option.value))
  return formatEnumLabel(value)
}

/**
 * Column header for a custom field. Definitions created outside the form
 * path store `label = fieldName` ("CONFERENCE_TRACK"), so a label that is
 * missing, equals the key, or reads as a machine constant is humanised.
 */
export function customFieldHeaderLabel(
  label: string | null | undefined,
  fieldName: string,
): string {
  const trimmed = label?.trim() ?? ''
  if (trimmed && trimmed !== fieldName && !isMachineConstant(trimmed)) {
    return trimmed
  }
  const source = trimmed || fieldName
  if (isMachineConstant(source)) return formatEnumLabel(source)
  // camelCase keys: "conferenceTrack" → "Conference Track"
  const spaced = source
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
  return spaced
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/**
 * Form label for a custom field. A definition without a display name (or
 * one whose label is the raw key, "field_1769016255096") reads as
 * "Untitled field" rather than the key (design audit S14).
 */
export function customFieldFormLabel(
  label: string | null | undefined,
  fieldName: string,
): string {
  const trimmed = label?.trim() ?? ''
  if (
    !trimmed ||
    trimmed === fieldName ||
    /^field_\d+$/i.test(trimmed) ||
    isMachineConstant(trimmed)
  ) {
    return 'Untitled field'
  }
  return trimmed
}
